/**
 * <collection-subheader>
 *
 * Handles clicks on the subheader tabs, fetches the target collection's
 * product grid via Shopify's Section Rendering API (?section_id=...), and
 * swaps it into the page without a full reload. Falls back to a normal
 * navigation if anything goes wrong.
 */
class CollectionSubheader extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.initScrollButtons();
  }

  initScrollButtons() {
    const tabsNav = this.querySelector('.collection-subheader__tabs');
    const prevBtn = this.querySelector('[data-subheader-tabs-prev]');
    const nextBtn = this.querySelector('[data-subheader-tabs-next]');

    if (!tabsNav || (!prevBtn && !nextBtn)) return;

    const scrollAmount = () => Math.min(tabsNav.clientWidth * 0.6, 240);

    const updateButtons = () => {
      const maxScrollLeft = tabsNav.scrollWidth - tabsNav.clientWidth;
      const scrollLeft = tabsNav.scrollLeft;
      if (prevBtn) prevBtn.disabled = scrollLeft <= 4;
      if (nextBtn) nextBtn.disabled = scrollLeft >= maxScrollLeft - 4;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        tabsNav.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        tabsNav.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    }

    tabsNav.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    requestAnimationFrame(updateButtons);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const tab = event.target.closest('[data-subheader-tab]');
    if (!tab) return;

    event.preventDefault();

    if (tab.classList.contains('is-active') || this.classList.contains('is-loading')) {
      return;
    }

    this.loadCollection(tab.dataset.tabUrl);
  }

  async loadCollection(url) {
    if (!url || !this.sectionId) {
      window.location.href = url;
      return;
    }

    const gridWrapper = document.querySelector('.collection_grid');

    this.classList.add('is-loading');
    if (gridWrapper) gridWrapper.classList.add('is-loading');

    try {
      const separator = url.includes('?') ? '&' : '?';
      const fetchUrl = `${url}${separator}section_id=${this.sectionId}`;
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`Collection subheader: request failed with status ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('.collection_grid');
      const currentGrid = document.querySelector('.collection_grid');

      if (!newGrid || !currentGrid) {
        throw new Error('Collection subheader: could not locate .collection_grid in the response');
      }

      currentGrid.replaceWith(newGrid);

      // Update active state on every tab instance on the page (desktop + mobile if duplicated)
      document.querySelectorAll('[data-subheader-tab]').forEach((tabEl) => {
        const isActive = tabEl.dataset.tabUrl === url;
        tabEl.classList.toggle('is-active', isActive);
        tabEl.setAttribute('aria-selected', String(isActive));
      });

      window.history.pushState({ collectionSubheaderUrl: url }, '', url);

      // Let other scripts (lazy-load images, badge animations, filtering re-init, analytics) know
      document.dispatchEvent(
        new CustomEvent('collection-subheader:updated', { detail: { url } })
      );
    } catch (error) {
      console.error(error);
      // Fail safe: if anything goes wrong, do a normal navigation instead of leaving a broken UI
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
      const refreshedGridWrapper = document.querySelector('.collection_grid');
      if (refreshedGridWrapper) refreshedGridWrapper.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('collection-subheader')) {
  customElements.define('collection-subheader', CollectionSubheader);
}

// Support the browser back/forward buttons after an ajax tab swap
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.collectionSubheaderUrl) {
    window.location.reload();
  }
});

/* =====================================================================
   Product grid reveal fix
   Paste this at the very END of collection-subheader.js (Shop All
   template) and collection-subheader-recipients.js (Recipients template).
   Nothing above it needs to change.

   Why: the theme hides product cards ([data-animate] => opacity .001)
   until its scroll-reveal script sees them. When a tab swaps in a new
   grid, the script doesn't notice the new cards until the page scrolls,
   so the grid looks empty.

   What it does, after a tab switch or page load:
   1. Nudges the theme's own scroll-reveal (keeps the normal fade-in).
   2. A moment later, force-shows any on-screen card that is still hidden.
   ===================================================================== */
(function () {
  if (window.__collectionGridRevealFix) return;
  window.__collectionGridRevealFix = true;

  function isOnScreen(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function hydrateImages(grid) {
    grid.querySelectorAll('img').forEach(function (img) {
      if (img.dataset.src && !img.getAttribute('src')) img.setAttribute('src', img.dataset.src);
      if (img.dataset.srcset && !img.getAttribute('srcset')) img.setAttribute('srcset', img.dataset.srcset);
    });
    grid.querySelectorAll('source[data-srcset]').forEach(function (source) {
      if (!source.getAttribute('srcset')) source.setAttribute('srcset', source.dataset.srcset);
    });
  }

  function revealHiddenOnScreen(grid) {
    grid.querySelectorAll('[data-animate]').forEach(function (el) {
      if (!isOnScreen(el)) return; // off-screen cards keep the theme's normal scroll reveal
      if (parseFloat(window.getComputedStyle(el).opacity) < 0.1) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      }
    });
    grid.querySelectorAll('img').forEach(function (img) {
      if (isOnScreen(img) && parseFloat(window.getComputedStyle(img).opacity) < 0.1) {
        img.style.opacity = '1';
      }
    });
  }

  function refreshGrid() {
    var grid = document.querySelector('.collection_grid');
    if (!grid) return;

    hydrateImages(grid);

    // 1. Let the theme's own scroll-reveal run first
    window.dispatchEvent(new Event('scroll'));
    window.dispatchEvent(new Event('resize'));

    // 2. Safety net for anything still hidden
    setTimeout(function () { revealHiddenOnScreen(grid); }, 350);
  }

  // After a tab switch (Shop All and Recipients templates)
  document.addEventListener('collection-subheader:updated', refreshGrid);
  document.addEventListener('collection-subheader-recipients:updated', refreshGrid);

  // On first page load
  if (document.readyState === 'complete') {
    refreshGrid();
  } else {
    window.addEventListener('load', refreshGrid);
  }
})();